import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit3, Layers, Save, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import api from '../services/api';
import IconButton from '../components/IconButton';

const Gruposegur = () => {
  const { user } = useAuth();
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingGrupo, setEditingGrupo] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    cod_grupo: '',
    desc_grupo: '',
    ativo_grupo: '1'
  });

  useEffect(() => { loadGrupos(); }, []);
  useEffect(() => { document.title = 'Grupo - EstOX'; }, []);

  const loadGrupos = async () => {
    try {
      const response = await api.get('/gruposegur');
      setGrupos(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar grupos segur:', error);
      toast.error('Erro ao carregar grupos');
    } finally {
      setLoading(false);
    }
  };

  const filtered = grupos.filter(g => (
    String(g.desc_grupo ?? '').toLowerCase().includes(String(searchTerm).toLowerCase()) ||
    String(g.cod_grupo ?? '').toLowerCase().includes(String(searchTerm).toLowerCase())
  ));

  const openModal = (g = null) => {
    setEditingGrupo(g);
    setFormData({
      cod_grupo: g?.cod_grupo || '',
      desc_grupo: g?.desc_grupo || '',
      ativo_grupo: g?.ativo_grupo != null ? String(g.ativo_grupo) : '1'
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGrupo(null);
    setFormData({ cod_grupo: '', desc_grupo: '', ativo_grupo: '1' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!String(formData.desc_grupo).trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }
    setIsSaving(true);
    try {
      const payload = { ...formData };
      if (editingGrupo) {
        await api.put(`/gruposegur/${editingGrupo.id_grupo}`, payload);
        toast.success('Grupo atualizado com sucesso!');
      } else {
        await api.post('/gruposegur', payload);
        toast.success('Grupo criado com sucesso!');
      }
      closeModal();
      loadGrupos();
    } catch (error) {
      console.error('Erro ao salvar grupo segur:', error);
      const msg = error?.response?.data?.message || 'Erro ao salvar grupo';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Grupo</h1>
        <p className="text-gray-600">Gerencie os grupos de seguro</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Layers className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Grupos</p>
              <p className="text-2xl font-bold text-gray-900">{grupos.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Search className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Filtrados</p>
              <p className="text-2xl font-bold text-gray-900">{filtered.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Plus className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ações</p>
              <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Novo Grupo" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar grupos..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Novo Grupo" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ativo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map((g) => (
                <tr key={g.id_grupo} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{g.id_grupo}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{g.cod_grupo ?? '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{g.desc_grupo || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {String(g.ativo_grupo) === '1' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">ATIVO</span>
                    ) : String(g.ativo_grupo) === '0' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">INATIVO</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex space-x-2">
                      <IconButton icon={Edit3} onClick={() => openModal(g)} variant="warning" size="sm" title="Editar" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Layers className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum grupo encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">{searchTerm ? 'Tente ajustar sua busca.' : 'Comece criando um novo grupo.'}</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{editingGrupo ? 'Editar Grupo' : 'Novo Grupo'}</h3>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                    <input type="number" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={formData.cod_grupo} onChange={(e) => setFormData({ ...formData, cod_grupo: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                    <input type="text" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={formData.desc_grupo} onChange={(e) => setFormData({ ...formData, desc_grupo: e.target.value })} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ativo</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={formData.ativo_grupo} onChange={(e) => setFormData({ ...formData, ativo_grupo: e.target.value })}>
                    <option value="1">ATIVO</option>
                    <option value="0">INATIVO</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <IconButton icon={X} onClick={closeModal} variant="secondary" title="Cancelar" />
                  <IconButton icon={Save} type="submit" disabled={isSaving} variant="primary" title={isSaving ? 'Salvando...' : (editingGrupo ? 'Atualizar' : 'Criar')} />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gruposegur;
